import { ReactElement, useEffect } from 'react';
import { useBlocker } from '@tanstack/react-router';
import { useTranslation } from '@frontend/i18n';
import { ConfirmationModal, useModalControl } from '@frontend/design-system';

interface UseLeavePageConfirmOptions {
  shouldBlockFn: () => boolean;
  title?: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  enableBeforeUnload?: boolean;
  deps?: React.DependencyList;
}

export interface UseLeavePageConfirmReturn {
  confirmationModal: ReactElement;
}

export const useLeavePageConfirm = (options: UseLeavePageConfirmOptions): UseLeavePageConfirmReturn => {
  const { t } = useTranslation('digital-forms-experience');
  const { shouldBlockFn, title, message, confirmLabel, cancelLabel, enableBeforeUnload = true, deps = [] } = options;

  const { modalProps, openModal, closeModal } = useModalControl();

  const { proceed } = useBlocker({
    shouldBlockFn: () => {
      if (shouldBlockFn()) {
        openModal();
        return true;
      }
      return false;
    },
    enableBeforeUnload: false, // We handle beforeunload separately to avoid conflicts
    withResolver: true,
  });

  // Handle beforeUnload event for browser refresh/close
  useEffect(() => {
    if (!enableBeforeUnload) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Only check the blocking condition, don't open our custom modal
      if (shouldBlockFn()) {
        e.preventDefault();
        e.returnValue = ''; // Required for some browsers to show native confirmation
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldBlockFn, enableBeforeUnload, ...deps]);

  const handleConfirm = () => {
    proceed?.();
    closeModal();
  };

  const confirmationModal: ReactElement = (
    <ConfirmationModal
      {...modalProps}
      title={title ?? t('You have unsaved changes')}
      message={message ?? t('Are you sure you want to leave? Your changes will be lost.')}
      confirmLabel={confirmLabel ?? t('Leave anyway')}
      cancelLabel={cancelLabel}
      onConfirm={handleConfirm}
      onCancel={closeModal}
      destructive
    />
  );

  return { confirmationModal };
};
